/**
 * The asset catalog (`/assets`, `asset.view`): one list card holding the toolbar, the assets as a table or tiles and
 * the pagination. Search, filters, sort and page live in the URL; searching and filtering run on the server.
 */

import { Link } from 'react-router-dom';
import { Library, Plus, SearchX } from 'lucide-react';

import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { PageHead } from '@/ui/PageHead';
import { Pagination } from '@/ui/Pagination';

import { CatalogTable, CatalogTiles } from './CatalogResults';
import { CatalogToolbar } from './CatalogToolbar';
import { type CatalogPageState, useCatalogPage } from './useCatalogPage';

function EmptyState({ state }: { state: CatalogPageState }) {
  if (state.filtered) {
    return (
      <div className="empty">
        <div className="e-icon"><SearchX aria-hidden="true" /></div>
        <h3>No assets match</h3>
        <p>Try other words, or clear the search and the filters to see the whole catalog.</p>
        <div className="empty-actions">
          <button type="button" className="btn btn-secondary" onClick={state.clearFilters}>Clear search and filters</button>
        </div>
      </div>
    );
  }
  return (
    <div className="empty">
      <div className="e-icon"><Library aria-hidden="true" /></div>
      <h3>The catalog is empty</h3>
      <p>{state.canCreate ? 'Add the first platform, accelerator, framework or offer GFT can reuse.' : 'Published assets will show here.'}</p>
      {state.canCreate && (
        <div className="empty-actions">
          <Link className="btn btn-primary" to="/assets/new"><Plus aria-hidden="true" /> New asset</Link>
        </div>
      )}
    </div>
  );
}

function Results({ state }: { state: CatalogPageState }) {
  const { list, view } = state;
  if (list.isPending) return <LoadingCard label="Loading assets" />;
  if (list.error) return <LoadFailure error={list.error} onRetry={() => void list.refetch()} />;
  const rows = list.data.assets;
  if (rows.length === 0) return <EmptyState state={state} />;
  return (
    <>
      {state.layout === 'tiles' ? <CatalogTiles state={state} rows={rows} /> : <CatalogTable state={state} rows={rows} />}
      <Pagination
        page={view.page}
        pageSize={view.size}
        total={list.data.total}
        noun={list.data.total === 1 ? 'asset' : 'assets'}
        onPage={(page) => state.update({ page })}
        onPageSize={(size) => state.update({ size })}
      />
    </>
  );
}

export function CatalogPage() {
  const state = useCatalogPage();
  return (
    <>
      <PageHead title="Assets" subtitle="What GFT can reuse, with the proof behind it" />
      <div className="card list-card catalog-card">
        <CatalogToolbar state={state} />
        <div className="catalog-body" aria-busy={state.list.isFetching}>
          <Results state={state} />
        </div>
      </div>
    </>
  );
}
